import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LoadingSpinner from "../Components/LoadingSpinner";
import quimeraOrderService from "../services/quimeraOrder.service";
import { RoutesPaths } from "../Router/constant";
import "./QuimeraOrders.css";

const readText = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value !== "object") return String(value);
  if (Array.isArray(value)) return value.map(readText).filter(Boolean).join(", ");
  return String(value.name || value.fullName || value.companyName || value.customerName || value.productName || value.title || value.label || value.value || value.email || "");
};

const firstValue = (object, keys) => {
  for (const key of keys) {
    const value = object?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

const parseOrder = (response) => {
  const body = response?.data ?? response;
  const candidates = [
    body?.order,
    body?.booking,
    body?.data?.order,
    body?.data?.booking,
    body?.data?.data?.order,
    body?.data?.data?.booking,
    body?.data?.data,
    body?.data,
    body?.result,
    body,
  ];
  if (Array.isArray(body)) return body[0] || null;
  return candidates.find((candidate) => candidate && typeof candidate === "object" && !Array.isArray(candidate)) || null;
};

const showDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? readText(value)
    : date.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
};

const errorMessage = (error) => error?.response?.data?.message || error?.response?.data?.error || error?.message || "Unable to load this booking.";

function DetailField({ label, value }) {
  const display = readText(value) || "Not provided";
  return <div className="quimera-detail-field"><span>{label}</span><strong>{display}</strong></div>;
}

export default function QuimeraOrderDetails() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let active = true;
    const loadOrder = async () => {
      setLoading(true);
      setError("");
      const token = quimeraOrderService.getSavedQuimeraToken();
      if (!token) {
        if (active) navigate(RoutesPaths.QUIMERA_ORDERS, { replace: true });
        return;
      }
      try {
        const verificationResponse = await quimeraOrderService.verifyQuimeraToken(token);
        if (quimeraOrderService.tokenIsRejected(verificationResponse)) {
          quimeraOrderService.clearQuimeraToken();
          if (active) navigate(RoutesPaths.QUIMERA_ORDERS, { replace: true });
          return;
        }
        const response = await quimeraOrderService.getQuimeraOrder(id, token);
        if (!active) return;
        const parsed = parseOrder(response);
        if (!parsed) throw new Error("Booking details were not included in the response.");
        setOrder(parsed);
      } catch (requestError) {
        if (!active) return;
        const status = requestError?.response?.status;
        if (status === 401 || status === 403) {
          quimeraOrderService.clearQuimeraToken();
          navigate(RoutesPaths.QUIMERA_ORDERS, { replace: true });
          return;
        }
        setOrder(null);
        setError(errorMessage(requestError));
      } finally {
        if (active) setLoading(false);
      }
    };
    loadOrder();
    return () => { active = false; };
  }, [id, navigate, reload]);

  const customer = order?.customer || order?.client || order?.buyer || order?.user || {};
  const shipment = order?.shipment || order?.shipmentDetails || order?.shipping || order?.logistics || {};
  const productList = order?.products || order?.items || order?.orderItems || order?.lines || [];
  const products = Array.isArray(productList) ? productList : [];
  const status = readText(firstValue(order, ["status", "bookingStatus", "orderStatus", "shipmentStatus", "currentStatus"])) || "Pending";
  const statusTone = /deliver|complete|success/i.test(status) ? "delivered" : /cancel|reject|fail/i.test(status) ? "cancelled" : /transit|progress|ship|dispatch/i.test(status) ? "in-progress" : "pending";
  const orderNumber = readText(firstValue(order, ["orderNumber", "bookingNumber", "bookingId", "trackingId", "referenceNumber", "orderId", "orderNo", "code", "_id", "id"])) || id;
  const productName = readText(order?.product || order?.commodity || products[0]?.product || products[0]?.item || products[0]) || readText(firstValue(order, ["productName", "commodityName", "itemName", "goods", "commodity"])) || "—";
  const rawQuantity = firstValue(order, ["quantity", "qty", "totalQuantity", "orderQuantity"]) ?? firstValue(products[0], ["quantity", "qty", "amount"]);
  const quantity = rawQuantity && typeof rawQuantity === "object"
    ? [readText(firstValue(rawQuantity, ["amount", "value", "quantity"])), readText(rawQuantity.unit || rawQuantity.quantityUnit)].filter(Boolean).join(" ")
    : [readText(rawQuantity), readText(firstValue(order, ["quantityUnit", "unit", "unitOfMeasure"]) || products[0]?.unit)].filter(Boolean).join(" ");

  return (
    <div className="dashboard-page quimera-page quimera-detail-page">
      <button type="button" className="quimera-back-link" onClick={() => navigate(RoutesPaths.QUIMERA_ORDERS)}>
        <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m12.5 4-6 6 6 6" /></svg>
        Back to Quimera orders
      </button>
      <header className="quimera-detail-heading">
        <div>
          <h1>Booking details</h1>
          <p>Shipment information and booking status.</p>
        </div>
        {!loading && order && <span className={`quimera-status-badge ${statusTone}`}><span />{status}</span>}
      </header>

      {loading ? (
        <section className="quimera-detail-panel"><LoadingSpinner label="Loading booking details..." /></section>
      ) : error ? (
        <section className="quimera-detail-panel quimera-detail-error" role="alert">
          <strong>Could not load booking details</strong>
          <span>{error}</span>
          <button type="button" onClick={() => setReload((current) => current + 1)}>Try again</button>
        </section>
      ) : order ? (
        <>
          <section className="quimera-detail-panel">
            <div className="quimera-detail-panel-heading"><h2>Booking information</h2><span>{orderNumber}</span></div>
            <div className="quimera-detail-grid">
              <DetailField label="Order ID" value={orderNumber} />
              <DetailField label="Date" value={showDate(firstValue(order, ["bookingDate", "bookedAt", "createdAt", "createdOn", "orderDate", "date"]))} />
              <DetailField label="Type" value={firstValue(order, ["type", "shippingType", "freightType", "transportType", "shippingMethod", "shipmentType", "mode"])} />
              <DetailField label="Reference" value={firstValue(order, ["referenceNumber", "reference", "trackingId", "trackingNumber"])} />
            </div>
          </section>

          <section className="quimera-detail-panel">
            <div className="quimera-detail-panel-heading"><h2>Customer</h2></div>
            <div className="quimera-detail-grid">
              <DetailField label="Name" value={firstValue(customer, ["fullName", "name", "customerName", "companyName"]) || firstValue(order, ["customerName", "clientName", "buyerName"])} />
              <DetailField label="Email" value={firstValue(customer, ["email", "emailAddress"]) || order?.customerEmail} />
              <DetailField label="Phone" value={firstValue(customer, ["phone", "phoneNumber", "mobile"]) || order?.customerPhone} />
              <DetailField label="Country" value={firstValue(customer, ["country", "countryName"]) || order?.customerCountry} />
            </div>
          </section>

          <section className="quimera-detail-panel">
            <div className="quimera-detail-panel-heading"><h2>Product and shipment</h2></div>
            <div className="quimera-detail-grid">
              <DetailField label="Product" value={productName} />
              <DetailField label="Quantity" value={quantity} />
              <DetailField label="Freight type" value={firstValue(shipment, ["type", "freightType", "transportType", "shippingMethod"]) || firstValue(order, ["type", "freightType", "transportType", "shippingMethod", "shipmentType"])} />
              <DetailField label="Origin" value={firstValue(shipment, ["origin", "originPort", "from", "fromAddress", "portOfLoading"]) || firstValue(order, ["origin", "originPort", "portOfLoading"])} />
              <DetailField label="Destination" value={firstValue(shipment, ["destination", "destinationPort", "to", "toAddress", "portOfDischarge"]) || firstValue(order, ["destination", "destinationPort", "portOfDischarge"])} />
              <DetailField label="Carrier / vessel" value={firstValue(shipment, ["carrier", "carrierName", "vessel", "vesselName"]) || firstValue(order, ["carrier", "carrierName", "vessel", "vesselName"])} />
              <DetailField label="Tracking number" value={firstValue(shipment, ["trackingNumber", "trackingId", "containerNumber"]) || firstValue(order, ["trackingNumber", "containerNumber"])} />
              <DetailField label="Estimated delivery" value={showDate(firstValue(shipment, ["estimatedDelivery", "eta", "arrivalDate"]) || firstValue(order, ["estimatedDelivery", "eta", "arrivalDate"]))} />
            </div>
          </section>

          {products.length > 1 && (
            <section className="quimera-detail-panel">
              <div className="quimera-detail-panel-heading"><h2>Items</h2></div>
              <div className="quimera-items-list">
                {products.map((item, index) => <div key={item?._id || item?.id || index}><strong>{readText(item?.product || item?.item || item?.name || item?.productName) || `Item ${index + 1}`}</strong><span>{readText(item?.quantity || item?.qty) || "Quantity not provided"}</span></div>)}
              </div>
            </section>
          )}
        </>
      ) : null}
    </div>
  );
}